import { FC } from 'react';

import Heading from '@/components/Heading';
import Text from '@/components/Text';
import { OrderSummeryBreakUpTitle, Payment } from '@/models/order';
import { getCurrencyFormat } from '@/utils/helper';
import { storage } from '@/config';

type PropTypes = {
  itemsCount?: number;
  grandTotal?: string | number;
  paymentDetails?: any;
  additionalPaymentDetails?: string;
  totalAmount?: number;
  isLoading?: boolean;
};

const Loader = () => (
  <div className='h-6 w-1/6 loading-color-transition rounded-2xl' />
);

const OrderSummary: FC<PropTypes> = ({
  itemsCount,
  paymentDetails,
  grandTotal,
  additionalPaymentDetails,
  totalAmount,
  isLoading = false,
}) => {
  const shippingAmount = paymentDetails?.others?.find(
    ({ code, value }: Payment) => code.toLowerCase() === 'shipping' && value
  )?.value;
  const convenienceFee = paymentDetails?.others?.find(
    ({ code, value }: Payment) => code.toLowerCase() === 'payment_fee' && value
  )?.value;

  const deliveryType = storage.get('deliveryType');

  const isHomeDeliveryFree = !shippingAmount && deliveryType === 'home';

  return (
    <div aria-label='order-summary' className='w-full px-3'>
      <div className='flex items-center justify-between gap-3'>
        <Heading className='p-0 text-base'>Order Summary</Heading>
        {!isLoading ? (
          <Text className='p-0 text-right text-sm'>
            {itemsCount} Item{itemsCount && itemsCount > 1 ? 's' : ''}
          </Text>
        ) : (
          <Loader />
        )}
      </div>

      {paymentDetails?.others?.filter(
        ({ code, value }: Payment) => value && code !== 'grand_total'
      ).length > 0 ? (
        <div
          className='my-3 space-y-1 border-y border-dew py-3'
          aria-label='order-breakup'>
          {paymentDetails?.others
            ?.filter(
              ({ code, value }: Payment) => value && code !== 'grand_total'
            )
            ?.map((el: any) =>
              el?.value ? (
                <div
                  className='flex items-center justify-between gap-3'
                  key={el?.code}>
                  <Text className='p-0 text-sm'>
                    {
                      OrderSummeryBreakUpTitle[
                        el?.code as keyof typeof OrderSummeryBreakUpTitle
                      ]
                    }
                  </Text>
                  {!isLoading ? (
                    <Text className='p-0 text-right text-black'>
                      {el?.code.toLowerCase() ===
                      OrderSummeryBreakUpTitle.shipping.toLowerCase()
                        ? '+'
                        : ''}
                      {getCurrencyFormat({ value: el?.value })}
                    </Text>
                  ) : (
                    <Loader />
                  )}
                </div>
              ) : null
            )}
          {!shippingAmount && convenienceFee ? (
            <div className='flex items-center justify-between gap-3'>
              <Text className='p-0 text-sm'>Convenience & Handling Fee</Text>
              {!isLoading ? (
                <Text className='p-0 text-right text-black'>
                  {getCurrencyFormat({ value: convenienceFee })}
                </Text>
              ) : (
                <Loader />
              )}
            </div>
          ) : null}
          {isHomeDeliveryFree ? (
            <div className='flex items-center justify-between gap-3'>
              <Text className='p-0 text-sm'>Convenience & Handling Fee</Text>
              {!isLoading ? (
                <div className='flex items-center justify-center gap-x-1'>
                  <Text className='p-0 text-right text-black line-through'>
                    {getCurrencyFormat({ value: 99 })}
                  </Text>
                  <Text className='p-0 text-right font-semibold text-black'>
                    FREE
                  </Text>
                </div>
              ) : (
                <Loader />
              )}
            </div>
          ) : null}
        </div>
      ) : (
        <div className='my-3 h-[0.7px] bg-dew' />
      )}

      <div aria-label='total' className='space-y-1'>
        <div className='flex items-center justify-between gap-3'>
          <Heading as='h4' className='p-0 text-base'>
            Grand Total
          </Heading>
          {!isLoading ? (
            <Heading as='h4' className='p-0 text-base'>
              {totalAmount
                ? getCurrencyFormat({ value: totalAmount })
                : getCurrencyFormat({
                    value: paymentDetails?.total?.value ?? grandTotal,
                    maximumFractionDigits: 0,
                  })}
            </Heading>
          ) : (
            <Loader />
          )}
        </div>
        <Text className='p-0 text-xs text-black/60'>
          {additionalPaymentDetails ??
            `Tax included. ${
              shippingAmount || convenienceFee || isHomeDeliveryFree
                ? 'Shipping included'
                : 'Shipping calculated at checkout'
            }.`}
        </Text>
      </div>
    </div>
  );
};

export default OrderSummary;
